import _styled from '@emotion/styled/base';
import { IconTableSortDescending, IconTableSortAscending } from '@hubble/icons/web';
import 'react';
import { ScreenReaderOnly } from '../ScreenReaderOnly/index.js';
import { TCellBase } from './styles.js';
import { testIds } from './testIds.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { Spacing } from '../primitives/spacing.js';
import { size, density, text } from '@hubble/tokens/web';

const ROW = 'row';
const ROW_REVERSE = 'row-reverse';
const SortToggle = _styled(TCellBase.withComponent('button', {
  target: "ev8m8ct4"
}), {
  target: "ev8m8ct3"
})("cursor:pointer;border:none;background:none;height:100%;width:100%;font-weight:inherit;font-size:inherit;line-height:inherit;flex-direction:", ({
  justifyContent
}) => justifyContent === 'end' ? ROW_REVERSE : ROW, ";justify-content:flex-start;padding:0;");
const THeaderCell = _styled(TCellBase.withComponent('th', {
  target: "ev8m8ct5"
}), {
  target: "ev8m8ct2"
})("max-height:", size[40], ";height:", p => density.contentItems.height[p.density] || size[40], ";font-size:", text.typesets.body.xsBold.fontSize, ";font-weight:", text.typesets.body.xsBold.fontWeight, ";line-height:", text.typesets.body.xsBold.lineHeight, ";z-index:0;", ({
  density,
  resizable
}) => `
    ${(resizable || Boolean(density)) && `
        position: relative;
        overflow: visible;
        padding-right: 0;
      ${resizable && `flex: none;`}
      ${density && resizable && `z-index: 1;`}
    `}
  `, " overflow:visible;", SortToggle, "{&:hover,&:focus[data-focus-visible-added]{outline:none;", ({
  density,
  theme
}) => !density ? `background: ${theme.colorScheme.list.background.hover};` : `
            &::before {
              content: "";
              position: absolute;
              inset: 0px;
              background: ${theme.colorScheme.list.background.hover};
              z-index: -1;
            }
      `, ";}}&:first-of-type{", ({
  density: density$1,
  resizable
}) => `
      ${!density$1 && `padding-inline-start: ${Spacing.scale[2.5]};`}
      ${SortToggle} {
        ${!density$1 && `margin-inline-start: -${Spacing.scale[2.5]};
          padding-inline-start: ${Spacing.scale[2.5]};`}

        &:hover,
        &:focus[data-focus-visible-added] {
          &::before {
            ${!resizable && `left: -${density.contentItems.padding.left[density$1]};`}
          }
        }
      }
    `, ";}&:last-of-type{", ({
  density: density$1,
  resizable
}) => `
      ${!density$1 && `padding-inline-end: ${Spacing.scale[2.5]};`}
      ${SortToggle} {
        ${!density$1 && `
          margin-inline-end: -${Spacing.scale[2.5]};
          padding-inline-end: ${Spacing.scale[2.5]};
        `}

        &:hover,
        &:focus[data-focus-visible-added] {
          &::before {
            ${!resizable && `right: -${density.contentItems.padding.right[density$1]};`}
          }
        }
      }
    `, ";}&:hover>.resizer{opacity:1;}");
const VISIBLE = 'visible';
const HIDDEN = 'hidden';
const IconWrapper = _styled('span', {
  target: "ev8m8ct1"
})("visibility:", ({
  visible
}) => visible ? VISIBLE : HIDDEN, ";line-height:0;");
const LabelWrapper = _styled('span', {
  target: "ev8m8ct0"
})("overflow:hidden;text-overflow:ellipsis;white-space:nowrap;", ({
  resizable,
  justifyContent
}) => resizable && justifyContent === 'end' && `padding-right: 4px;`, ";");
const SORT_DIR_MAP = {
  asc: 'ascending',
  desc: 'descending'
};
function HeaderCell({
  children,
  justifyContent = 'start',
  sortDirection,
  visiblyHidden,
  onSortingChange,
  density,
  resizeHandle,
  ...rest
}) {
  const label = jsx(LabelWrapper, {
    resizable: Boolean(resizeHandle),
    justifyContent: justifyContent,
    children: children
  });
  const canSort = typeof onSortingChange === 'function';
  const ariaSort = sortDirection ? SORT_DIR_MAP[sortDirection] : canSort ? 'none' : undefined;
  const Icon = sortDirection === 'desc' ? IconTableSortDescending : IconTableSortAscending;
  const headerContent = !visiblyHidden && canSort ? jsxs(SortToggle, {
    "data-testid": testIds.sortableHeaderToggle,
    justifyContent: justifyContent,
    onClick: onSortingChange,
    density: density,
    children: [label, jsx(IconWrapper, {
      visible: Boolean(sortDirection),
      "data-testid": testIds.sortIcon + ariaSort,
      "aria-hidden": true,
      children: jsx(Icon, {
        size: "xs"
      })
    })]
  }) : label;
  return jsxs(THeaderCell, {
    ...rest,
    justifyContent: justifyContent,
    "aria-sort": ariaSort,
    title: typeof children === 'string' ? children : undefined,
    density: density,
    resizable: Boolean(resizeHandle),
    children: [visiblyHidden ? jsx(ScreenReaderOnly, {
      "data-testid": testIds.hiddenColumnHeader,
      children: headerContent
    }) : headerContent, resizeHandle && !visiblyHidden && resizeHandle]
  });
}

export { HeaderCell };
